<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Timeline } from '#library/components.schema.build.ts'
	import Pagination from '#components/pagination.svelte'
	import emblaCarouselSvelte from 'embla-carousel-svelte'
	import { setupTweenOpacity } from '#library/emblaTween.ts'
	import type { EmblaCarouselType } from 'embla-carousel'
	import CardInfo from '#components/card_info.svelte'

	interface Props {
		blok: Blok<Timeline>
	}

	let { blok }: Props = $props()

	let active = $state(0)
	let embla_api: EmblaCarouselType | undefined = $state()

	function onInit(event: CustomEvent) {
		embla_api = event.detail
		if (!embla_api) return

		const removeTweenOpacity = setupTweenOpacity(embla_api)

		embla_api.on('select', () => {
			active = embla_api?.selectedScrollSnap() ?? 0
		})

		embla_api.on('destroy', removeTweenOpacity)
	}

	const items = $derived(blok.items ?? [])
</script>

<section class="timeline {blok.theme}" {...attrs(blok)}>
	<div class="head contain">
		{#if blok.title?.text}
			<svelte:element this={blok.title?.tag || 'div'} class="title">
				{blok.title.text}
			</svelte:element>
		{/if}
		<Pagination
			items={items?.length}
			onlyarrows
			onprev={embla_api?.scrollPrev}
			onnext={embla_api?.scrollNext}
			{active}
		/>
	</div>
	<div class="contain" use:emblaCarouselSvelte onemblaInit={onInit}>
		<div class="cards">
			{#each items as { suptitle, title, body, image, theme, shape }, i (i)}
				<div class="item">
					<CardInfo {suptitle} {title} {body} {image} {theme} {shape} {i} count={items?.length} inline />
				</div>
			{/each}
		</div>
	</div>
	<div class="markers contain">
		{#each items as { title, marker }, i (i)}
			{#if marker}
				<button class="marker" class:active={active === i} onclick={() => embla_api?.scrollTo(i)}>
					{title}
				</button>
			{/if}
		{/each}
	</div>
	<div class="line contain">
		{#each items as { title, marker }, i (i)}
			{#if marker}
				<button
					class="dot"
					class:active={active === i}
					onclick={() => embla_api?.scrollTo(i)}
					aria-label={title}
				></button>
			{/if}
		{/each}
	</div>
</section>

<style lang="css">
	.timeline {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.head {
		align-items: center;
		display: flex;
		justify-content: space-between;
		margin-block-end: 4rem;
	}

	.title {
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
	}

	.cards {
		align-items: center;
		display: flex;
	}

	.item {
		align-self: stretch;
		display: flex;
		flex: 1 0 90%;

		@media (--from-mobile) {
			flex: 1 0 75%;
		}

		:global(.card) {
			@media (--until-mobile) {
				display: flex;
				flex-direction: column;
			}
		}
	}

	.markers {
		align-items: center;
		display: flex;
		justify-content: space-between;
		margin-block-start: 4rem;
	}

	.marker {
		font: 500 var(--f-h6);
		letter-spacing: var(--ls-h6);
		transition: scale 0.2s ease;

		&.active {
			scale: 1.5;
		}
	}

	.line {
		align-items: center;
		display: flex;
		justify-content: space-between;
		margin-block-start: 2rem;
		padding-inline: 1rem;

		&::before {
			background-color: hsl(0 0% 100% / 0.6);
			content: '';
			display: block;
			inset: 50% 50% auto;
			position: absolute;
			translate: -50% -50%;
			size: 1px 100%;
			width: calc(100% - 4rem);
		}

		.dot {
			background-color: hsl(0 0% 100% / 0.6);
			backdrop-filter: blur(4px);
			border-radius: 50%;
			size: 1.125rem;
			transition: background-color 0.2s ease;

			&::before {
				content: '✦';
				font-size: 2rem;
				opacity: 0;
				position: absolute;
				inset: 50% auto auto 50%;
				scale: 0;
				translate: -50% -50%;
				transition:
					opacity 0.2s ease,
					scale 0.2s ease;
			}

			&.active {
				background-color: transparent;

				&::before {
					opacity: 1;
					scale: 1;
				}
			}
		}
	}
</style>
